import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { AxiosError } from 'axios';
import type {
  ICurrencyBody,
  ICurrencyDetail,
  ICurrencyDetailResponse,
  ICurrencyListResponse,
  ICurrencyUpdateBody,
} from '@/types/currency.types';
import { CurrencyService } from '@/services/currency.services';

/**
 * Store pour gérer les  currencies et leurs actions associées
 */
export const useCurrencyStore = defineStore('currency', () => {
  /**
   * Crée une nouvelle devise via le service distant.
   *
   * Cette fonction envoie les données d'une devise au backend pour
   * l'enregistrement dans la base de données.
   *
   * @async
   * @function createCurrency
   * @param {ICurrencyBody} data - Données de la devise à créer (label, name, description).
   * @returns {Promise<any>} Réponse de l'API contenant les informations de la devise créée.
   * @throws {AxiosError} Lève une erreur si la requête échoue ou si le service renvoie une erreur.
   */
  const createCurrency = async (data: ICurrencyBody): Promise<any> => {
    try {
      const response = await CurrencyService.createCurrency(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * État réactif contenant la liste paginée des devises récupérées depuis l’API.
   *
   * Ce ref stocke la réponse typée de l’API après l’appel à `getCurrency`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<ICurrencyListResponse | null>}
   */
  const dataToGet = ref<ICurrencyListResponse | null>(null);

  /**
   * Récupère la liste des devises depuis le service distant.
   *
   * Cette fonction effectue une requête HTTP via `CurrencyService`
   * pour récupérer la liste paginée des devises disponibles,
   * puis met à jour l’état réactif `dataToGet` avec les données renvoyées.
   *
   * @async
   * @function getCurrency
   * @returns {Promise<ICurrencyListResponse>} Promesse résolue contenant les données des devises récupérées.
   * @throws {AxiosError} Lève une erreur si la récupération échoue.
   */
  const getCurrency = async (): Promise<ICurrencyListResponse> => {
    try {
      const response = await CurrencyService.getCurrency();
      dataToGet.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * État réactif contenant les informations d’une devise récupérée depuis l’API.
   *
   * Ce `ref` stocke la réponse typée de l’API après l’appel à `getCurrencyById`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<ICurrencyDetailResponse | null>}
   */
  const dataToGetById = ref<ICurrencyDetailResponse | null>(null);

  /**
   * Récupère une devise spécifique depuis le service distant par son identifiant (code).
   *
   * Cette fonction effectue une requête HTTP via `CurrencyService`
   * pour obtenir les informations complètes de la devise, puis met à jour
   * l’état réactif `dataToGetById` avec les données reçues.
   *
   * @async
   * @function getCurrencyById
   * @returns {Promise<ICurrencyDetailResponse>} Promesse résolue contenant les données de la devise récupérée.
   * @throws {AxiosError} Lève une erreur si la récupération échoue.
   */
  const getCurrencyById = async (): Promise<ICurrencyDetailResponse> => {
    try {
      const response = await CurrencyService.getCurrencyById();
      dataToGetById.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * Met à jour une devise spécifique sur le service distant.
   *
   * Cette fonction effectue une requête HTTP via `CurrencyService`
   * pour modifier les informations d’une devise existante,
   * puis renvoie les données mises à jour.
   *
   * @async
   * @function updateCurrency
   * @param {Partial<ICurrencyUpdateBody>} updateData - Données partielles de la devise à mettre à jour.
   * @returns {Promise<ICurrencyDetail>} Promesse résolue contenant les données mises à jour de la devise.
   * @throws {AxiosError} Lève une erreur si la mise à jour échoue.
   */
  const updateCurrency = async (
    updateData: Partial<ICurrencyUpdateBody>,
  ): Promise<ICurrencyDetail> => {
    try {
      const response = await CurrencyService.updateCurrency(updateData);
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * Supprime une devise spécifique via le service distant.
   *
   * Cette fonction envoie une requête HTTP à l’aide de `CurrencyService`
   * pour retirer une devise existante,
   * puis renvoie la réponse du serveur en cas de succès.
   *
   * @async
   * @function deleteCurrency
   * @returns {Promise<any>} Promesse résolue contenant la réponse du serveur après suppression.
   * @throws {AxiosError} Lève une erreur si la suppression échoue.
   */
  const deleteCurrency = async (): Promise<any> => {
    try {
      const response = await CurrencyService.deleteCurrency();
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  return {
    createCurrency,
    getCurrency,
    dataToGet,
    getCurrencyById,
    dataToGetById,
    updateCurrency,
    deleteCurrency,
  };
});
